/**
 * PAPPO — AdminPage
 * Painel do administrador (só role admin): fila de KYC (aprovar/rejeitar com
 * imagens decifradas), break-glass (janela de 5 min) e crypto-shredding.
 * Feedback inline (sem toast), no padrão do Painel Parental.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  IdCard,
  Check,
  XCircle,
  KeyRound,
  Trash2,
  Eye,
} from 'lucide-react';
import {
  listarKycPendentes,
  carregarKycImagem,
  aprovarKyc,
  rejeitarKyc,
  abrirBreakGlass,
  obterBreakGlassDado,
  esquecerUsuario,
} from '../services/admin';
import type { KycPendente } from '../types/kyc';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import styles from './AdminPage.module.css';

type Tab = 'kyc' | 'breakglass';

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('kyc');

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.titleRow}>
          <ShieldCheck size={28} className={styles.titleIcon} />
          <div>
            <h1 className={styles.title}>Administração</h1>
            <p className={styles.subtitle}>
              Revisão de identidade (Nível 01) e acessos de emergência
            </p>
          </div>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'kyc' ? styles.tabActive : ''}`}
          onClick={() => setTab('kyc')}
        >
          <IdCard size={16} /> Verificações KYC
        </button>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'breakglass' ? styles.tabActive : ''}`}
          onClick={() => setTab('breakglass')}
        >
          <KeyRound size={16} /> Break-glass
        </button>
      </div>

      {tab === 'kyc' ? <KycQueue /> : <BreakGlassPanel />}
    </div>
  );
}

/* ── Fila de verificações KYC ─────────────────────────────── */
function KycQueue() {
  const [pendentes, setPendentes] = useState<KycPendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [images, setImages] = useState<Record<number, { rg?: string; selfie?: string }>>({});
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listarKycPendentes();
    setLoading(false);
    if (res.success && res.data) {
      setPendentes(res.data.pendentes);
      setError('');
    } else {
      setError(res.error ?? 'Falha ao carregar a fila de verificações');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpand = useCallback(
    async (item: KycPendente) => {
      if (expandedId === item.id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(item.id);
      if (!images[item.id]) {
        const [rg, selfie] = await Promise.all([
          carregarKycImagem(item.id, 'rg'),
          carregarKycImagem(item.id, 'selfie'),
        ]);
        setImages((prev) => ({
          ...prev,
          [item.id]: { rg: rg ?? undefined, selfie: selfie ?? undefined },
        }));
      }
    },
    [expandedId, images]
  );

  const handleApprove = useCallback(
    async (id: number) => {
      setBusy(true);
      const res = await aprovarKyc(id);
      setBusy(false);
      if (res.success) {
        setExpandedId(null);
        await load();
      } else {
        setError(res.error ?? 'Falha ao aprovar');
      }
    },
    [load]
  );

  const handleReject = useCallback(
    async (id: number) => {
      if (!rejectReason.trim()) {
        setError('Informe o motivo da rejeição.');
        return;
      }
      setBusy(true);
      const res = await rejeitarKyc(id, rejectReason.trim());
      setBusy(false);
      if (res.success) {
        setRejectId(null);
        setRejectReason('');
        setExpandedId(null);
        await load();
      } else {
        setError(res.error ?? 'Falha ao rejeitar');
      }
    },
    [rejectReason, load]
  );

  if (loading) return <p className={styles.empty}>Carregando fila…</p>;

  return (
    <section className={styles.section}>
      {error && (
        <div className={styles.errorBox} role="alert">
          {error}
        </div>
      )}
      {pendentes.length === 0 ? (
        <p className={styles.empty}>Nenhuma verificação pendente. 🎉</p>
      ) : (
        <div className={styles.list}>
          {pendentes.map((item) => (
            <div key={item.id} className={styles.card}>
              <div className={styles.cardRow}>
                <div className={styles.info}>
                  <span className={styles.name}>{item.display_name}</span>
                  <span className={styles.meta}>
                    @{item.username} · {item.email} · {item.role}
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  leftIcon={<Eye size={14} />}
                  onClick={() => toggleExpand(item)}
                >
                  {expandedId === item.id ? 'Ocultar' : 'Ver documentos'}
                </Button>
              </div>

              {expandedId === item.id && (
                <div className={styles.docs}>
                  <figure className={styles.doc}>
                    <figcaption className={styles.docLabel}>RG</figcaption>
                    {images[item.id]?.rg ? (
                      <img className={styles.docImg} src={images[item.id]!.rg} alt="RG" />
                    ) : (
                      <p className={styles.empty}>Carregando/indisponível…</p>
                    )}
                  </figure>
                  <figure className={styles.doc}>
                    <figcaption className={styles.docLabel}>Selfie</figcaption>
                    {images[item.id]?.selfie ? (
                      <img className={styles.docImg} src={images[item.id]!.selfie} alt="Selfie" />
                    ) : (
                      <p className={styles.empty}>Carregando/indisponível…</p>
                    )}
                  </figure>
                </div>
              )}

              {rejectId === item.id ? (
                <div className={styles.rejectBox}>
                  <Input
                    label="Motivo da rejeição (obrigatório)"
                    placeholder="Ex: foto ilegível"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  <div className={styles.actions}>
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => {
                        setRejectId(null);
                        setRejectReason('');
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="danger"
                      isLoading={busy}
                      onClick={() => handleReject(item.id)}
                    >
                      Confirmar rejeição
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={styles.actions}>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<XCircle size={14} />}
                    onClick={() => {
                      setRejectId(item.id);
                      setError('');
                    }}
                  >
                    Rejeitar
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<Check size={14} />}
                    isLoading={busy}
                    onClick={() => handleApprove(item.id)}
                  >
                    Aprovar (Nível 01)
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ── Break-glass + crypto-shredding ───────────────────────── */
function BreakGlassPanel() {
  const [form, setForm] = useState({ target_user: '', data_type: 'phone', reason: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [janela, setJanela] = useState<{ id: number; expires_at: string | null } | null>(null);
  const [dado, setDado] = useState<string | null>(null);

  // Crypto-shredding
  const [forgetTarget, setForgetTarget] = useState('');
  const [forgetConfirm, setForgetConfirm] = useState('');
  const [forgetMsg, setForgetMsg] = useState('');
  const [forgetBusy, setForgetBusy] = useState(false);

  const abrir = useCallback(async () => {
    setError('');
    setDado(null);
    const target = parseInt(form.target_user, 10);
    if (!target || !form.reason.trim()) {
      setError('Informe o ID do usuário alvo e o motivo (obrigatório).');
      return;
    }
    setBusy(true);
    const res = await abrirBreakGlass({
      target_user: target,
      data_type: form.data_type,
      reason: form.reason.trim(),
    });
    setBusy(false);
    if (res.success && res.data) {
      setJanela(res.data);
    } else {
      setError(res.error ?? 'Falha ao abrir break-glass');
    }
  }, [form]);

  const verDado = useCallback(async () => {
    if (!janela) return;
    setError('');
    const res = await obterBreakGlassDado(janela.id);
    if (res.success && res.data) {
      setDado(res.data.valor || '(vazio)');
    } else {
      setError(res.error ?? 'Falha ao obter o dado (janela pode ter expirado)');
    }
  }, [janela]);

  const esquecer = useCallback(async () => {
    setForgetMsg('');
    const target = parseInt(forgetTarget, 10);
    if (!target) {
      setForgetMsg('Informe o ID do usuário.');
      return;
    }
    if (forgetConfirm.trim().toUpperCase() !== 'ESQUECER') {
      setForgetMsg('Digite ESQUECER para confirmar esta ação irreversível.');
      return;
    }
    setForgetBusy(true);
    const res = await esquecerUsuario(target);
    setForgetBusy(false);
    if (res.success) {
      setForgetMsg(`DEK destruída e conta ${target} desativada.`);
      setForgetTarget('');
      setForgetConfirm('');
    } else {
      setForgetMsg(res.error ?? 'Falha ao esquecer usuário');
    }
  }, [forgetTarget, forgetConfirm]);

  return (
    <section className={styles.section}>
      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>
          <KeyRound size={18} /> Break-glass (acesso de emergência)
        </h2>
        <p className={styles.hint}>
          Abre uma janela de <strong>5 minutos</strong> para acessar um dado cifrado
          de um usuário. Motivo é obrigatório e tudo fica na trilha de auditoria.
        </p>
        {error && (
          <div className={styles.errorBox} role="alert">
            {error}
          </div>
        )}
        <Input
          label="ID do usuário alvo"
          placeholder="Ex: 42"
          value={form.target_user}
          onChange={(e) => setForm((f) => ({ ...f, target_user: e.target.value }))}
        />
        <label className={styles.fieldLabel}>Tipo de dado</label>
        <select
          className={styles.select}
          value={form.data_type}
          onChange={(e) => setForm((f) => ({ ...f, data_type: e.target.value }))}
        >
          <option value="phone">Telefone</option>
          <option value="kyc_rg">Documento RG</option>
          <option value="kyc_selfie">Selfie</option>
        </select>
        <Input
          label="Motivo (obrigatório)"
          placeholder="Ex: solicitação judicial nº..."
          value={form.reason}
          onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
        />
        <div className={styles.actions}>
          <Button isLoading={busy} onClick={abrir}>
            Abrir janela
          </Button>
        </div>

        {janela && (
          <div className={styles.windowBox}>
            <p className={styles.meta}>
              Janela #{janela.id} aberta
              {janela.expires_at ? ` — expira ${new Date(janela.expires_at).toLocaleTimeString()}` : ''}.
            </p>
            <div className={styles.actions}>
              <Button size="sm" variant="ghost" leftIcon={<Eye size={14} />} onClick={verDado}>
                Ver dado agora
              </Button>
            </div>
            {dado !== null && (
              <pre className={styles.dado}>{dado}</pre>
            )}
          </div>
        )}
      </div>

      <div className={`${styles.card} ${styles.danger}`}>
        <h2 className={styles.sectionTitle}>
          <Trash2 size={18} /> Esquecer usuário (crypto-shredding)
        </h2>
        <p className={styles.hint}>
          Destrói a chave de dados (DEK) do usuário — os dados cifrados ficam
          <strong> permanentemente ilegíveis</strong> — e desativa a conta. Não deleta linhas.
        </p>
        {forgetMsg && (
          <div className={styles.errorBox} role="alert">
            {forgetMsg}
          </div>
        )}
        <Input
          label="ID do usuário"
          placeholder="Ex: 42"
          value={forgetTarget}
          onChange={(e) => setForgetTarget(e.target.value)}
        />
        <Input
          label='Digite "ESQUECER" para confirmar'
          placeholder="ESQUECER"
          value={forgetConfirm}
          onChange={(e) => setForgetConfirm(e.target.value)}
        />
        <div className={styles.actions}>
          <Button variant="danger" isLoading={forgetBusy} onClick={esquecer}>
            Esquecer usuário
          </Button>
        </div>
      </div>
    </section>
  );
}
